export const WORKSPACE_CHART_HYDRATION_RETRY_DELAYS_MS = [350, 1200];
export const WORKSPACE_CHART_UNAVAILABLE_RECOVERY_DELAY_MS = 30_000;
export const WORKSPACE_CHART_SNAPSHOT_INTERVAL_TOLERANCE = 5;
export const WORKSPACE_CHART_SNAPSHOT_MIN_TOLERANCE_MS = 3 * 60 * 1000;
export const WORKSPACE_CHART_SNAPSHOT_PRICE_PADDING_RATIO = 0.1;

const chartBarTime = (bar) => bar?.datetime || bar?.date || bar?.x || null;
const INTRADAY_SCALE_DURATION_MS = {
  "15s": 15 * 1000,
  "30s": 30 * 1000,
  "1m": 60 * 1000,
  "1min": 60 * 1000,
  "2m": 2 * 60 * 1000,
  "2min": 2 * 60 * 1000,
  "3m": 3 * 60 * 1000,
  "3min": 3 * 60 * 1000,
  "5m": 5 * 60 * 1000,
  "5min": 5 * 60 * 1000,
  "10m": 10 * 60 * 1000,
  "15m": 15 * 60 * 1000,
  "30m": 30 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "2h": 2 * 60 * 60 * 1000,
  "3h": 3 * 60 * 60 * 1000,
  "4h": 4 * 60 * 60 * 1000,
};

const timestampMs = (value) => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;
  return Date.parse(value);
};

const US_EQUITY_ASSET_TYPES = new Set([
  "stock",
  "stocks",
  "equity",
  "equities",
  "fund",
  "funds",
  "etf",
  "etfs",
  "index",
  "indices",
]);
const US_EQUITY_EXTENDED_ASSET_TYPES = new Set([
  "stock",
  "stocks",
  "equity",
  "equities",
  "fund",
  "funds",
  "etf",
  "etfs",
]);
const CONTINUOUS_ASSET_TYPES = new Set([
  "crypto",
  "cryptos",
  "cryptocurrency",
  "cryptocurrencies",
  "forex",
  "future",
  "futures",
]);
const ACTIVE_EXTENDED_EQUITY_SESSIONS = new Set([
  "premarket",
  "regular",
  "afterhours",
]);
const SNAPSHOT_PRICE_WINDOW_BARS = 120;

const finiteMarketNumber = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(String(value).replace(/[%$,]/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const snapshotWallClockIsRelevant = ({ assetType, marketSession }) => {
  const normalizedAssetType = String(assetType || "").trim().toLowerCase();
  if (CONTINUOUS_ASSET_TYPES.has(normalizedAssetType)) return true;
  if (!US_EQUITY_ASSET_TYPES.has(normalizedAssetType)) return false;
  if (US_EQUITY_EXTENDED_ASSET_TYPES.has(normalizedAssetType)) {
    return ACTIVE_EXTENDED_EQUITY_SESSIONS.has(marketSession);
  }
  return marketSession === "regular";
};

const snapshotPriceEnvelope = (bars) => {
  const recentBars = bars.slice(-SNAPSHOT_PRICE_WINDOW_BARS);
  let low = null;
  let high = null;
  recentBars.forEach((bar) => {
    [bar?.open, bar?.high, bar?.low, bar?.close].forEach((value) => {
      const number = finiteMarketNumber(value);
      if (number == null) return;
      low = low == null ? number : Math.min(low, number);
      high = high == null ? number : Math.max(high, number);
    });
  });
  return low == null || high == null ? null : { low, high };
};

export const workspaceChartSnapshotAssessment = ({
  bars,
  scale,
  assetType = null,
  currentPrice = null,
  quoteAt = null,
  now = Date.now(),
  marketSession = null,
} = {}) => {
  const snapshotBars = Array.isArray(bars) ? bars : [];
  if (snapshotBars.length < 2) {
    return { eligible: false, reason: "insufficient_bars" };
  }

  const normalizedScale = String(scale || "").trim();
  const durationMs = INTRADAY_SCALE_DURATION_MS[normalizedScale] || null;
  const tailTimeMs = timestampMs(chartBarTime(snapshotBars.at(-1)));
  if (!Number.isFinite(tailTimeMs)) {
    return { eligible: false, reason: "missing_tail_time" };
  }

  if (durationMs) {
    const quoteReferenceMs = timestampMs(quoteAt);
    const compareWithWallClock = snapshotWallClockIsRelevant({
      assetType,
      marketSession,
    });
    const referenceTimeMs = Number.isFinite(quoteReferenceMs)
      ? quoteReferenceMs
      : compareWithWallClock
        ? timestampMs(now)
        : null;
    const toleranceMs = Math.max(
      durationMs * WORKSPACE_CHART_SNAPSHOT_INTERVAL_TOLERANCE,
      WORKSPACE_CHART_SNAPSHOT_MIN_TOLERANCE_MS,
    );
    if (
      Number.isFinite(referenceTimeMs) &&
      referenceTimeMs - tailTimeMs > toleranceMs
    ) {
      return {
        eligible: false,
        reason: "stale_tail",
        tailGapMs: referenceTimeMs - tailTimeMs,
        toleranceMs,
      };
    }
  }

  const livePrice = finiteMarketNumber(currentPrice);
  const envelope = livePrice == null ? null : snapshotPriceEnvelope(snapshotBars);
  if (envelope) {
    const span = Math.max(envelope.high - envelope.low, 0);
    const padding = Math.max(
      span * WORKSPACE_CHART_SNAPSHOT_PRICE_PADDING_RATIO,
      Math.max(Math.abs(livePrice), 1) * 0.0005,
    );
    if (
      livePrice < envelope.low - padding ||
      livePrice > envelope.high + padding
    ) {
      return {
        eligible: false,
        reason: "price_dislocation",
        livePrice,
        low: envelope.low,
        high: envelope.high,
        padding,
      };
    }
  }

  return { eligible: true, reason: "presentation_safe" };
};

export const workspaceChartHydrationSeed = ({
  key,
  cachedBars,
  scale,
  assetType = null,
  currentPrice = null,
  quoteAt = null,
  now = Date.now(),
  marketSession = null,
} = {}) => {
  const bars = Array.isArray(cachedBars) ? cachedBars : [];
  const assessment = workspaceChartSnapshotAssessment({
    bars,
    scale,
    assetType,
    currentPrice,
    quoteAt,
    now,
    marketSession,
  });
  return assessment.eligible
    ? {
        key,
        status: "refreshing",
        bars,
        snapshotDecision: assessment.reason,
      }
    : {
        key,
        status: "loading",
        bars: [],
        snapshotDecision: assessment.reason,
      };
};

export const workspaceChartLiveGap = ({
  bars,
  scale,
  now = Date.now(),
} = {}) => {
  const durationMs = INTRADAY_SCALE_DURATION_MS[String(scale || "").trim()];
  if (!durationMs || !Array.isArray(bars) || bars.length === 0) return null;

  let latestBar = null;
  for (let index = bars.length - 1; index >= 0; index -= 1) {
    if (chartBarTime(bars[index])) {
      latestBar = bars[index];
      break;
    }
  }
  const latestTimeMs = timestampMs(chartBarTime(latestBar));
  const nowMs = timestampMs(now);
  if (!Number.isFinite(latestTimeMs) || !Number.isFinite(nowMs)) return null;

  const latestBucketMs = Math.floor(latestTimeMs / durationMs) * durationMs;
  const currentBucketMs = Math.floor(nowMs / durationMs) * durationMs;
  let previousBucketMs = null;
  for (let index = bars.length - 2; index >= 0; index -= 1) {
    const time = timestampMs(chartBarTime(bars[index]));
    if (!Number.isFinite(time)) continue;
    const bucketMs = Math.floor(time / durationMs) * durationMs;
    if (bucketMs < latestBucketMs) {
      previousBucketMs = bucketMs;
      break;
    }
  }
  const adjacentElapsedBuckets = Number.isFinite(previousBucketMs)
    ? Math.floor((latestBucketMs - previousBucketMs) / durationMs)
    : 0;
  if (adjacentElapsedBuckets > 1) {
    return {
      key: [
        String(scale || ""),
        previousBucketMs,
        latestBucketMs,
      ].join(":"),
      latestBucketMs,
      currentBucketMs,
      missingBucketCount: adjacentElapsedBuckets - 1,
      source: "series_tail",
    };
  }

  const elapsedBuckets = Math.floor(
    (currentBucketMs - latestBucketMs) / durationMs,
  );
  if (elapsedBuckets <= 1) return null;

  return {
    key: [
      String(scale || ""),
      latestBucketMs,
      currentBucketMs,
    ].join(":"),
    latestBucketMs,
    currentBucketMs,
    missingBucketCount: elapsedBuckets - 1,
    source: "wall_clock",
  };
};

export const WORKSPACE_CHART_SETTLEMENT_INTERVAL_MS = 60_000;
export const WORKSPACE_CHART_SETTLEMENT_LAG_MS = 2 * 60_000;
export const WORKSPACE_CHART_SETTLEMENT_LOOKBACK_MS = 12 * 60_000;

export const workspaceChartSettlementWindow = ({ scale, now = Date.now() } = {}) => {
  if (!INTRADAY_SCALE_DURATION_MS[String(scale || "").trim()]) return null;
  const nowMs = timestampMs(now);
  if (!Number.isFinite(nowMs)) return null;

  const toMs = Math.floor(
    (nowMs - WORKSPACE_CHART_SETTLEMENT_LAG_MS) / 60_000,
  ) * 60_000;
  return {
    from: new Date(toMs - WORKSPACE_CHART_SETTLEMENT_LOOKBACK_MS).toISOString(),
    to: new Date(toMs).toISOString(),
  };
};

export const mergeWorkspaceChartBars = (...collections) => {
  const byTime = new Map();
  collections.flat().forEach((bar) => {
    const time = chartBarTime(bar);
    if (!time) return;
    byTime.set(String(time), bar);
  });
  return Array.from(byTime.values()).sort((left, right) => {
    const leftTime = Date.parse(chartBarTime(left));
    const rightTime = Date.parse(chartBarTime(right));
    if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
      return leftTime - rightTime;
    }
    return String(chartBarTime(left)).localeCompare(String(chartBarTime(right)));
  });
};

export const reconcileWorkspaceChartTail = ({
  existingBars,
  authoritativeBars,
} = {}) => {
  const current = Array.isArray(existingBars) ? existingBars : [];
  const authoritative = Array.isArray(authoritativeBars)
    ? authoritativeBars
    : [];
  if (authoritative.length === 0) return current;

  const firstAuthoritativeTime = timestampMs(
    chartBarTime(authoritative[0]),
  );
  if (!Number.isFinite(firstAuthoritativeTime)) {
    return mergeWorkspaceChartBars(current, authoritative);
  }

  const provenPrefix = current.filter((bar) => {
    const time = timestampMs(chartBarTime(bar));
    return Number.isFinite(time) && time < firstAuthoritativeTime;
  });
  return mergeWorkspaceChartBars(provenPrefix, authoritative);
};

// Settlement responses cover an intentionally delayed window. Replace only the
// timestamps that response actually proves; live bars after its cutoff are a
// separate, newer authority and must survive the merge.
export const reconcileWorkspaceChartSettlement = ({
  existingBars,
  authoritativeBars,
} = {}) => {
  const current = Array.isArray(existingBars) ? existingBars : [];
  const authoritative = Array.isArray(authoritativeBars)
    ? authoritativeBars
    : [];
  if (authoritative.length === 0) return current;

  const authoritativeTimestamps = new Set(
    authoritative
      .map((bar) => timestampMs(chartBarTime(bar)))
      .filter(Number.isFinite),
  );
  const retained = current.filter((bar) => {
    const time = timestampMs(chartBarTime(bar));
    return !Number.isFinite(time) || !authoritativeTimestamps.has(time);
  });
  return mergeWorkspaceChartBars(retained, authoritative);
};

export const earliestWorkspaceChartBarCursor = (bars) => {
  const first = Array.isArray(bars) ? bars[0] : null;
  return chartBarTime(first);
};

export const workspaceChartPresentationForTransition = ({
  currentPresentation = null,
  lastReadyPresentation = null,
  isHydrating = false,
  chartIdentityKey = null,
  tickerId = null,
  scale = null,
  retainPreviousInterval = false,
}) => {
  if (currentPresentation) {
    return { presentation: currentPresentation, retained: false };
  }

  const requestedIdentity = chartIdentityKey == null
    ? null
    : String(chartIdentityKey);
  const requestedTickerId = tickerId == null ? null : String(tickerId);
  const retainedIdentity = lastReadyPresentation?.identityKey == null
    ? null
    : String(lastReadyPresentation.identityKey);
  const retainedTickerId = lastReadyPresentation?.tickerId == null
    ? null
    : String(lastReadyPresentation.tickerId);
  const requestedScale = scale == null ? null : String(scale);
  const retainedScale = lastReadyPresentation?.scale == null
    ? null
    : String(lastReadyPresentation.scale);
  const canRetain =
    isHydrating &&
    lastReadyPresentation &&
    requestedIdentity &&
    requestedTickerId &&
    requestedScale &&
    retainedIdentity === requestedIdentity &&
    retainedTickerId === requestedTickerId &&
    (retainedScale === requestedScale || retainPreviousInterval);

  return canRetain
    ? { presentation: lastReadyPresentation, retained: true }
    : { presentation: null, retained: false };
};

const waitFor = (delayMs) =>
  new Promise((resolve) => window.setTimeout(resolve, delayMs));

export const fetchWorkspaceChartBarsWithRetry = async ({
  fetchBars,
  retryDelaysMs = WORKSPACE_CHART_HYDRATION_RETRY_DELAYS_MS,
  wait = waitFor,
  shouldContinue = () => true,
}) => {
  let lastRows = [];
  let lastError = null;

  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    if (!shouldContinue()) return lastRows;

    try {
      const bars = await fetchBars();
      lastRows = Array.isArray(bars) ? bars : [];
      lastError = null;
      if (lastRows.length >= 2) return lastRows;
    } catch (error) {
      lastError = error;
    }

    const retryDelay = retryDelaysMs[attempt];
    if (retryDelay == null || !shouldContinue()) break;
    await wait(retryDelay);
  }

  if (lastError) throw lastError;
  return lastRows;
};
